/**
 * DeferredSchemas — Renders children after the main content has painted.
 * This prevents 445+ SEO schema components from blocking First Contentful Paint.
 * Google's WRS will still see them since it waits for JS execution.
 */
import { useState, useEffect, ReactNode } from 'react';

interface DeferredSchemasProps {
  children: ReactNode;
  /** Delay in ms before rendering schemas (default: 100) */
  delay?: number;
}

export function DeferredSchemas({ children, delay = 100 }: DeferredSchemasProps) {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    // Use requestIdleCallback if available, otherwise setTimeout
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(() => setShouldRender(true), { timeout: delay + 500 });
      return () => cancelIdleCallback(id);
    } else {
      const timer = setTimeout(() => setShouldRender(true), delay);
      return () => clearTimeout(timer);
    }
  }, [delay]);

  if (!shouldRender) return null;
  return <>{children}</>;
}
